import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ClassQuery } from '../class/class.query';
import { ClassAssignmentQuery } from './class-assignment.query';
import { ClassAssignmentStoreModel, ClassAssignmentUIModel } from './class-assignment.model';

export type AssignmentRouteInput =
  | Pick<ClassAssignmentUIModel, 'phase' | 'slug' | 'class_id'>
  | Pick<ClassAssignmentStoreModel, 'status' | 'slug' | 'class_id'>;

const ROOT_CLASSES = 'classes';
const ROOT_ASSIGNMENTS = 'assignments';

@Injectable({ providedIn: 'root' })
export class AssignmentNavigationService {
  constructor(
    private readonly router: Router,
    private readonly classQuery: ClassQuery,
    private readonly classAssignmentQuery: ClassAssignmentQuery,
  ) {}
  navigateToAssignment(assignment: AssignmentRouteInput, options?: { edit?: boolean; replaceUrl?: boolean }): void {
    this.router.navigate(this.getAssignmentRoute(assignment, options), {
      replaceUrl: options?.replaceUrl,
    });
  }

  navigateToAssignmentById(assignmentId: string, options?: { edit?: boolean; replaceUrl?: boolean }): void {
    const assignment = this.classAssignmentQuery.getEntity(assignmentId);
    if (!assignment) {
      return;
    }
    this.navigateToAssignment(assignment, options);
  }

  navigateToClassOrFallback(classId?: string): void {
    const classSlug = classId ? this.classQuery.getEntity(classId)?.slug : this.classQuery.getActive()?.slug;
    this.router.navigate(this.getClassRoute(classSlug));
  }

  navigateBack(): void {
    const assignment = this.classAssignmentQuery.getActive();
    if (!assignment) {
      this.navigateToClassOrFallback();
      return;
    }

    const isDraft = assignment.status === 'DRAFT';
    if (isDraft) {
      this.navigateToClassOrFallback(assignment.class_id);
    } else {
      this.navigateToAssignment(assignment, { edit: false, replaceUrl: true });
    }
  }

  private getAssignmentRoute(assignment: AssignmentRouteInput, options?: { edit?: boolean }): string[] {
    const isDraft = 'phase' in assignment ? assignment.phase === 'DRAFT' : assignment.status === 'DRAFT';
    const shouldEdit = options?.edit ?? isDraft;
    const classSlug = this.classQuery.getEntity(assignment.class_id)?.slug;

    if (classSlug) {
      const base = ['/', ROOT_CLASSES, classSlug, ROOT_ASSIGNMENTS, assignment.slug];
      return shouldEdit ? [...base, 'edit'] : base;
    }

    const flatBase = ['/', ROOT_ASSIGNMENTS, assignment.slug];
    return shouldEdit ? [...flatBase, 'edit'] : flatBase;
  }

  private getClassRoute(classSlug?: string): string[] {
    return classSlug ? ['/', ROOT_CLASSES, classSlug] : ['/', ROOT_CLASSES];
  }
}
